import { useEffect, useMemo, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";
import { Currency } from "shared/utils/Currency";
import ComboFilters from "../ComboFilters";
import { Numbers } from "shared/utils/Numbers";
import { formatByType } from "../Reports";

export default function Recurrency({ data, hasReportFilters }) {
  const [selectedOption, setSelectedOption] = useState({
    name: "Faturamento",
    id: "recurrent_total",
  });

  const _data = {
    recurrent_total: {
      series: [
        "Faturamento dos primeiros pedidos",
        "Faturamento dos pedidos recorrentes",
      ],
      value_type: "currency",
      colors: ["#E900A7", "#5BF0BF"],
      data: [
        data.map((d) => d.first_total.value || 0),
        data.map((d) => d.recurrent_total.value || 0),
      ],
    },
    first_count: {
      series: [
        "Quantidade de primeiros pedidos",
        "Quantidade de pedidos recorrentes",
      ],
      value_type: "int",
      colors: ["#E900A7", "#5BF0BF"],
      data: [
        data.map((d) => d.first_count.value || 0),
        data.map((d) => d.recurrent_count.value || 0),
      ],
    },
  };

  const totalData = [];

  const getDataByIndex = (index) => {
    return data[index];
  };

  const memoData = useMemo(() => data, [JSON.stringify(data)]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("recurrency-chart");
      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });
      const selectedOptionId = selectedOption.id;

      const options = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "none",
          },
          backgroundColor: "#2B2B2B",
          borderRadius: 8,
          padding: [8, 8],
          borderWidth: 0,
          textStyle: {
            color: "#FFF",
            fontSize: 10,
          },
          formatter: (params) => {
            const index = params[0].dataIndex;
            const item = getDataByIndex(index);

            const formatGrowth = (growth) => {
              if (growth === 0 || hasReportFilters) return "";

              const isPositive = growth > 0;
              const color = isPositive ? "#51D9A3" : "#e48186";
              const sign = isPositive ? "+" : "";
              const arrow = isPositive ? "↑" : "↓";

              return `
                <span style="color:${color};font-weight:800;margin-left:4px;">
                  ${sign}${(growth || 0).toFixed(2)}% ${arrow}
                </span>
              `;
            };

            return `
              <div>
                ${
                  selectedOption.id === "recurrent_total"
                    ? `<div style="background-color: rgba(97, 97, 255, 0.1);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#6D6DF2;font-weight:700">
                    Faturamento total:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(
                      +item.recurrent_total.value + +item.first_total.value
                    )} 
                  </span>  
                </div>
                <div style="background-color: rgba(233, 0, 167, 0.12);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#E900A7;font-weight:700">
                    Faturamento dos primeiros pedidos:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(+item.first_total.value)} 
                  </span>  
                </div>

                <div style="background-color: rgba(91, 240, 191, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#5BF0BF;font-weight:700">
                    Faturamento dos pedidos recorrentes:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(+item.recurrent_total.value)} 
                  </span>  
                </div>`
                    : ""
                }

                                ${
                                  selectedOption.id === "first_count"
                                    ? `
                <div style="background-color: rgba(233, 0, 167, 0.12);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#E900A7;font-weight:700">
                    Quantidade de primeiros pedidos:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Numbers.formatInt(+item.first_count.value)} 
                  </span>  
                </div>

                <div style="background-color: rgba(91, 240, 191, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#5BF0BF;font-weight:700">
                    Quantidade de pedidos recorrentes:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Numbers.formatInt(+item.recurrent_count.value)} 
                  </span>  
                </div>`
                                    : ""
                                }
              </div>
            `;
          },
        },
        grid: {
          left: 0,
          right: 10,
          top: 30,
          bottom: 0,
          containLabel: true,
        },
        xAxis: [
          {
            type: "category",
            data: data.map((d) => d.date),
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            axisLine: {
              lineStyle: {
                color: "rgba(217, 217, 217, 0.5)",
              },
            },
            splitLine: {
              lineStyle: {
                color: "rgba(51, 51, 51, 0.55)",
                width: 1,
                type: "solid",
              },
            },
            axisLabel: {
              formatter: (value) =>
                ["first_count", "recurrent_count"].includes(selectedOption.id)
                  ? Numbers.formatInt(value)
                  : Currency.formatCurrency(value),
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        series: _data[selectedOptionId].series.map((name, sid, series) => {
          return {
            name,
            type: "bar",
            stack: "total",
            barWidth: "80%",
            data: _data[selectedOptionId].data[sid].map((d, did) =>
              totalData[did] <= 0 ? 0 : d
            ),
            label: {
              show: sid == series.length - 1,
              position: "top",
              lineHeight: 20,
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: 10,
              fontWeight: "semibold",
              formatter: (params) => {
                const index = params.dataIndex;
                let total;
                if (selectedOptionId === "first_count") {
                  total = _data[selectedOptionId].data.reduce(
                    (acc, curr) => +acc + +curr[index],
                    0
                  );
                } else {
                  total = _data[selectedOptionId].data.reduce(
                    (acc, curr) => +acc + +curr[index],
                    0
                  );
                }

                if (total <= 0) return "";
                return formatByType(total, _data[selectedOptionId].value_type);
              },
            },
            itemStyle: {
              color: _data[selectedOptionId].colors[sid],
            },
          };
        }),
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [memoData, selectedOption]);

  const allEmpty = _data[selectedOption.id].data.every((c) =>
    c.every((d) => d === 0 || !d)
  );

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between mb-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Recorrência</p>
        </div>
        <ComboFilters
          options={[
            {
              name: "Faturamento",
              id: "recurrent_total",
            },
            { name: "Quantidade", id: "first_count" },
          ]}
          onSelect={(option) => setSelectedOption(option)}
          selectedOption={selectedOption}
        />
      </div>

      <div className={styles.chart}>
        {allEmpty ? (
          <div className={styles.emptyChart} style={{ height: 350 }}>
            <span className="text-grey">Não há dados neste período</span>
          </div>
        ) : null}

        <div id="recurrency-chart" style={{ width: "100%", height: 350 }} />
      </div>
    </div>
  );
}
